import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { verifierApi, fileUrl } from '../../api/client';
import {
  ChevronLeft, Check, X, RotateCcw, MessageSquare, Lock, FileCheck, Hourglass,
  CheckCircle2, Flame, RefreshCw, ArrowRight, Scale, ClipboardList,
} from 'lucide-react';
import StatusBadge from '../../components/dashboard/StatusBadge';
import DashToolbar from '../../components/dashboard/DashToolbar';
import DashModal from '../../components/dashboard/DashModal';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import PasswordChangeForm from '../../components/dashboard/PasswordChangeForm';
import ProfileView from '../../components/dashboard/ProfileView';
import './LegalSpacePages.css';
import '../admin/AdminHome.css';

const LAYWER_CHECKLIST = [
  'Terminologie juridique correcte',
  'Noms et identités corrects',
  'Dates correctes',
  'Numéros/références corrects',
  'Articles et clauses correctement traduits',
  'Cohérence entre original et traduction',
];

const NOTARY_CHECKLIST = [
  'Identité des parties correcte',
  'Dates correctes',
  'Références de l\'acte correctes',
  'Informations relatives au bien correctes',
  'Terminologie notariale correcte',
  'Informations essentielles cohérentes',
  'Structure de l\'acte respectée',
];

function defaultChecklist(type) {
  const items = type === 'notary' ? NOTARY_CHECKLIST : LAYWER_CHECKLIST;
  return items.map((label) => ({ label, checked: false }));
}

function DocumentList({ docs, title }) {
  const list = (docs || []).filter((d) => d.url);
  if (!list.length) return null;
  return (
    <div className="dash-form-section">
      <h4>{title}</h4>
      <div className="dash-file-list">
        {list.map((d) => (
          <div key={d.id} className="dash-file-item">
            <span>{d.name}</span>
            <a className="dash-link" href={fileUrl(d.url)} target="_blank" rel="noopener noreferrer">Voir</a>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LegalHomePage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const d = t.dashboard;
  const [data, setData] = useState(null);

  useEffect(() => {
    verifierApi.overview().then(setData).catch(() => setData(null));
  }, []);

  const stats = data?.stats || {};
  const urgentReviews = data?.urgentReviews || [];
  const recentReviews = data?.recentReviews || [];

  const reload = () => verifierApi.overview().then(setData).catch(() => setData(null));

  const kpis = [
    { icon: FileCheck, label: l.statToReview, value: stats.toReview || 0, hint: l.filterPending || '', cls: 'ah-kpi--amber' },
    { icon: RotateCcw, label: l.statCorrections, value: stats.corrections || 0, hint: l.filterCorrection || '', cls: 'ah-kpi--violet' },
    { icon: CheckCircle2, label: l.statValidated, value: stats.validated || 0, hint: l.filterValidated || '', cls: 'ah-kpi--green' },
    { icon: Hourglass, label: l.statDueSoon, value: stats.dueSoon || 0, hint: '', cls: `ah-kpi--navy${stats.dueSoon ? ' is-hot' : ''}` },
  ];

  const reviewItem = (r) => (
    <div className="ah-queue-item" key={r.id}>
      <div>
        <strong>{r.id} · {r.document}</strong>
        <p>{r.clientName} · {r.deadline || '—'} · {r.langs}</p>
      </div>
      <Link className="dash-link" to={`/legal/verifications/${r.id}`}>{d.view}</Link>
    </div>
  );

  return (
    <div className="ah">
      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow"><Scale size={13} />{l.title}</span>
          <h1>{l.homeTitle}</h1>
          <p>{l.homeSubtitle}</p>
        </div>
        <div className="ah-hero-actions">
          <Link to="/legal/verifications" className="ah-hero-btn ah-hero-btn--gold">
            <FileCheck size={16} />{l.recentVerifications}
          </Link>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={reload}>
            <RefreshCw size={16} />{d.refresh || 'Actualiser'}
          </button>
        </div>
      </header>

      <div className="ah-kpis">
        {kpis.map((k) => (
          <div className={`ah-kpi ${k.cls}`} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
              {k.hint ? <small>{k.hint}</small> : null}
            </div>
          </div>
        ))}
      </div>

      <div className="ah-grid">
        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Flame size={16} /></span>
              <h2>{l.urgentVerifications}</h2>
              <span className="ah-card-count">{urgentReviews.length}</span>
            </div>
            <div className="ah-card-body">
              {urgentReviews.length ? <div className="ah-queue">{urgentReviews.map(reviewItem)}</div> : <p className="ah-empty">{d.empty}</p>}
            </div>
          </section>
        </div>

        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><ClipboardList size={16} /></span>
              <h2>{l.recentVerifications}</h2>
              <Link className="dash-link" to="/legal/verifications">
                {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
              </Link>
            </div>
            <div className="ah-card-body">
              {recentReviews.length ? <div className="ah-queue">{recentReviews.map(reviewItem)}</div> : <p className="ah-empty">{d.empty}</p>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function LegalVerificationsPage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const d = t.dashboard;
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState('all');
  const reviewsPg = usePagination(reviews, 6);

  useEffect(() => {
    verifierApi.verifications(status === 'all' ? '' : status).then((res) => setReviews(res.reviews || [])).catch(() => setReviews([]));
  }, [status]);

  const options = [
    { key: 'all', label: l.filterAll },
    { key: 'pending', label: l.filterPending },
    { key: 'correction_requested', label: l.filterCorrection },
    { key: 'validated', label: l.filterValidated },
  ];

  return (
    <>
      <header className="dash-page-head">
        <h1>{l.verificationsTitle}</h1>
        <p>{l.verificationsSubtitle}</p>
      </header>

      <DashToolbar>
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            className={`dash-filter-chip${status === o.key ? ' is-active' : ''}`}
            onClick={() => setStatus(o.key)}
          >
            {o.label}
          </button>
        ))}
      </DashToolbar>

      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>{d.client.colId}</th>
              <th>{l.colDocument}</th>
              <th>{l.colLangs}</th>
              <th>{l.colClient}</th>
              <th>{l.colType}</th>
              <th>{l.colStatus}</th>
              <th>{l.colDeadline}</th>
              <th>{d.actions}</th>
            </tr>
          </thead>
          <tbody>
            {reviews.length ? reviewsPg.pageItems.map((r) => (
              <tr key={r.id}>
                <td>{r.id}</td>
                <td>{r.document}</td>
                <td>{r.langs}</td>
                <td>{r.clientName}</td>
                <td>{r.type}</td>
                <td><StatusBadge status={r.status} labels={d.status} /></td>
                <td>{r.deadline || '—'}</td>
                <td><Link className="dash-link" to={`/legal/verifications/${r.id}`}>{d.view}</Link></td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="dash-empty-cell">{d.empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={reviewsPg.page} totalPages={reviewsPg.totalPages} total={reviewsPg.total} onChange={reviewsPg.setPage} />
    </>
  );
}

export function LegalVerificationDetailPage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const d = t.dashboard;
  const { id } = useParams();
  const [review, setReview] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [action, setAction] = useState(null);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const refresh = () => verifierApi.verification(id).then((res) => {
    setReview(res.review);
    setChecklist(res.review?.checklist?.length ? res.review.checklist : defaultChecklist(res.review?.type));
  }).catch(() => setReview(null));

  useEffect(() => {
    refresh();
  }, [id]);

  const submit = async (status) => {
    setError('');
    const needsReason = ['correction_requested', 'rejected'].includes(status);
    if (needsReason && !reason.trim()) {
      setError(l.reasonRequired);
      return;
    }
    try {
      const res = await verifierApi.updateStatus(id, status, needsReason ? reason : '', checklist);
      setReview(res.review);
      setChecklist(res.review?.checklist || []);
      setReason('');
      setAction(null);
    } catch (err) {
      setError(err.data?.error || 'Erreur');
    }
  };

  const addComment = async () => {
    if (!comment.trim()) {
      setError(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setError('');
    const res = await verifierApi.addComment(id, comment);
    setReview(res.review);
    setComment('');
  };

  const toggleCheck = (idx) => {
    setChecklist((prev) => prev.map((c, i) => (i === idx ? { ...c, checked: !c.checked } : c)));
  };

  if (!review) return <p className="dash-empty">{d.empty}</p>;

  const originals = review.documents.filter((d) => d.kind === 'original');
  const translated = review.documents.filter((d) => d.kind === 'translated');

  return (
    <>
      <header className="dash-page-head">
        <Link to="/legal/verifications" className="dash-back"><ChevronLeft size={16} /> {l.verificationsTitle}</Link>
        <h1>{l.detailTitle} {review.id}</h1>
        <p>{l.detailSubtitle}</p>
      </header>

      <div className="dash-panel">
        <div className="dash-detail-grid">
          <div><strong>{l.colDocument}</strong><p>{review.document}</p></div>
          <div><strong>{l.colLangs}</strong><p>{review.langs}</p></div>
          <div><strong>{l.colClient}</strong><p>{review.clientName}</p></div>
          <div><strong>{l.colType}</strong><p>{review.type}</p></div>
          <div><strong>{l.colDeadline}</strong><p>{review.deadline || '—'}</p></div>
          <div><strong>{l.colStatus}</strong><p><StatusBadge status={review.status} labels={d.status} /></p></div>
        </div>
      </div>

      <div className="dash-split">
        <DocumentList docs={originals} title={l.originalDocs} />
        <DocumentList docs={translated} title={l.translatedDocs} />
      </div>

      <div className="dash-panel">
        <h3>{l.checklist}</h3>
        <ul className="dash-checklist">
          {checklist.map((c, i) => (
            <li key={i}>
              <label className="dash-check-label">
                <input type="checkbox" checked={c.checked} onChange={() => toggleCheck(i)} />
                {c.label}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div className="dash-panel">
        <h3>{l.comments}</h3>
        {(review.comments || []).map((c, i) => (
          <div key={i} className="dash-comment">
            <strong>{c.author}</strong>
            <span>{new Date(c.createdAt).toLocaleString()}</span>
            <p>{c.body}</p>
          </div>
        ))}
        <div className="dash-comment-form">
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder={l.addComment} />
          <button type="button" className="dash-btn" onClick={addComment}><MessageSquare size={16} /> {d.send}</button>
        </div>
        {error && <p className="auth-error" role="alert">{error}</p>}
      </div>

      {['pending', 'in_review', 'correction_requested'].includes(review.status) && (
        <div className="dash-panel dash-actions">
          {action ? (
            <div className="dash-form-group">
              <label>{action === 'correction' ? l.correctionReason : l.rejectReason}</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} />
              {error && <p className="auth-error" role="alert">{error}</p>}
              <div className="dash-btn-row">
                <button type="button" className="dash-btn dash-btn--primary" onClick={() => submit(action === 'correction' ? 'correction_requested' : 'rejected')}><Check size={16} /> {action === 'correction' ? l.requestCorrection : l.reject}</button>
                <button type="button" className="dash-btn dash-btn--ghost" onClick={() => { setAction(null); setError(''); }}>{d.cancel}</button>
              </div>
            </div>
          ) : (
            <div className="dash-btn-row">
              <button type="button" className="dash-btn dash-btn--primary" onClick={() => submit('validated')}><Check size={16} /> {l.validate}</button>
              <button type="button" className="dash-btn dash-btn--warning" onClick={() => setAction('correction')}><RotateCcw size={16} /> {l.requestCorrection}</button>
              <button type="button" className="dash-btn dash-btn--danger" onClick={() => setAction('rejected')}><X size={16} /> {l.reject}</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function LegalProfilePage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const { user } = useAuth();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const space = user?.role === 'notary' ? 'notary' : user?.role === 'lawyer' ? 'lawyer' : 'legal';

  return (
    <>
      <header className="dash-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>{l.profileTitle}</h1>
          <p>{l.profileSubtitle}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setPasswordOpen(true)}>
          <Lock size={15} /> {t.auth.changePassword || 'Changer le mot de passe'}
        </button>
      </header>

      <ProfileView user={user} space={space} />

      <DashModal
        open={passwordOpen}
        title={t.auth.changePassword || 'Changer le mot de passe'}
        onClose={() => setPasswordOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>
    </>
  );
}

export function LegalNotificationsPage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const reload = () => verifierApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));
  useEffect(() => {
    reload();
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, []);

  const openNotif = (n) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      verifierApi.readNotifications({ id: n.id }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await verifierApi.readNotifications();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{l.notificationsTitle || 'Notifications'}</h1>
        <p>{l.notificationsSubtitle || 'Vos alertes.'}</p>
      </header>
      <NotificationsPanel
        items={items}
        onOpen={openNotif}
        onMarkAll={markAll}
        labels={{ all: 'Toutes', unread: 'Non lues', markAllRead: l.markAllRead || 'Tout marquer comme lu', empty: d.empty }}
      />
    </>
  );
}

export function LegalMessagesPage() {
  const { t } = useLanguage();
  const l = t.dashboard.legal;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const msgsPg = usePagination(messages, 6);

  const reload = () => verifierApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));
  useEffect(() => { reload(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await verifierApi.sendMessage({ body });
      setBody('');
      reload();
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{l.messagesTitle || 'Messages'}</h1><p>{l.messagesSubtitle || 'Échangez avec l\'administrateur.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <div className="dash-chat">
          {messages.length === 0 ? <p className="dash-empty">{d.empty}</p> : (
            msgsPg.pageItems.map((m) => (
              <div key={m.id} className={`dash-chat-bubble${m.from?.role === 'lawyer' || m.from?.role === 'notary' ? ' is-mine' : ''}`}>
                <strong>{m.from?.name || '—'}</strong><p>{m.body}</p><small>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</small>
              </div>
            ))
          )}
        </div>
        <Pagination page={msgsPg.page} totalPages={msgsPg.totalPages} total={msgsPg.total} onChange={msgsPg.setPage} />
        <form className="dash-form" onSubmit={send} noValidate style={{ marginTop: 16, maxWidth: '100%' }}>
          <div className="dash-field"><label>{l.messageLabel || 'Message'}</label><textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required /></div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{l.sendMessage || 'Envoyer'}</button>
        </form>
      </section>
    </>
  );
}
